import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  type AxiosError,
} from 'axios';
import { env } from '../../config/env';
import type { ApiResponse } from '../../types';
import { useAuthStore } from '../../stores/auth';
import { isTokenExpired } from '../../utils/jwt';

// Create axios instance
const axiosInstance: AxiosInstance = axios.create({
  baseURL: env.apiUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach access token from store
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor — handle 401 with store-based refresh
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    const isAuthEndpoint =
      originalRequest.url?.includes('/api/v1/auth/login') ||
      originalRequest.url?.includes('/api/v1/auth/refresh');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      // Pre-validate refresh token before attempting network call
      const { refreshToken } = useAuthStore.getState();
      if (!refreshToken || isTokenExpired(refreshToken)) {
        handleAuthFailure();
        return Promise.reject(error);
      }

      // Delegate to store — concurrent calls are deduplicated via shared promise
      const refreshed = await useAuthStore.getState().refreshTokens();

      if (refreshed) {
        // Retry original request — request interceptor will pick up the new token
        return axiosInstance(originalRequest);
      }

      handleAuthFailure();
      return Promise.reject(error);
    }

    // Extract error message from API response
    if (error.response?.data) {
      const apiError = error.response.data as { error?: { message?: string; code?: string } | string; message?: unknown };
      let message = 'Request failed';
      if (typeof apiError.error === 'object' && typeof apiError.error?.message === 'string') {
        message = apiError.error.message;
      } else if (typeof apiError.error === 'string') {
        message = apiError.error;
      } else if (typeof apiError.message === 'string') {
        message = apiError.message;
      }
      return Promise.reject(new Error(message));
    }

    // Handle network errors or errors without response data
    const message = error.message || 'Network error - please check your connection';
    return Promise.reject(new Error(message));
  }
);

function handleAuthFailure() {
  useAuthStore.setState({
    user: null,
    accessToken: null,
    refreshToken: null,
    isAuthenticated: false,
    error: null,
  });
  window.location.href = '/login';
}

// API client wrapper
export const apiClient = {
  get: async <T>(url: string, params?: object): Promise<T> => {
    const response = await axiosInstance.get<ApiResponse<T>>(url, { params });
    return response.data.data;
  },

  post: async <T>(url: string, data?: object): Promise<T> => {
    const response = await axiosInstance.post<ApiResponse<T>>(url, data);
    return response.data.data;
  },

  put: async <T>(url: string, data?: object): Promise<T> => {
    const response = await axiosInstance.put<ApiResponse<T>>(url, data);
    return response.data.data;
  },

  patch: async <T>(url: string, data?: object): Promise<T> => {
    const response = await axiosInstance.patch<ApiResponse<T>>(url, data);
    return response.data.data;
  },

  delete: async <T>(url: string, data?: object): Promise<T> => {
    const response = await axiosInstance.delete<ApiResponse<T>>(url, { data });
    return response.data.data;
  },

  getBlob: async (url: string, params?: object): Promise<Blob> => {
    const response = await axiosInstance.get(url, { params, responseType: 'blob' });
    return response.data as Blob;
  },
};

export default apiClient;
